import { axiosInstance } from "./index";
import { isApiErrorCode } from "@/shared/utils/apiError";

export type PaymentRoute = "cpi_direct" | "local_partner";

/** One date-effective row of country tax configuration, as any authenticated user may see it. */
export interface CountryTaxConfig {
	id: string;
	countryCode: string;
	taxName: string;
	taxNameLong: string;
	taxAuthorityName: string;
	registrationNumberLabel: string;
	registrationNumberPattern: string | null;
	/** Fractions, e.g. 0.1 for 10%. Null when the country has no such tax. */
	statutoryRate: number | null;
	effectiveRate: number | null;
	bandPoints: number;
	paymentRoute: PaymentRoute;
	partnerName: string | null;
	/** YYYY-MM-DD inclusive; null = since forever. */
	validFrom: string | null;
	/** YYYY-MM-DD exclusive; null = until further notice. */
	validTo: string | null;
	createdAt: string;
}

/** The admin view adds the audit fields. */
export interface CountryTaxConfigRow extends CountryTaxConfig {
	notes: string | null;
	createdBy: string | null;
	/** Name of the creating user (null for seed rows). */
	createdByName: string | null;
}

export interface CountryTaxConfigInput {
	countryCode: string;
	taxName: string;
	taxNameLong: string;
	taxAuthorityName: string;
	registrationNumberLabel: string;
	registrationNumberPattern: string | null;
	statutoryRate: number | null;
	effectiveRate: number | null;
	bandPoints: number;
	paymentRoute: PaymentRoute;
	partnerName: string | null;
	/** YYYY-MM-DD, today or later. */
	validFrom: string;
	notes: string;
}

export type CountryTaxConfigErrorCode =
	| "COUNTRY_INVALID"
	| "RATE_RANGE"
	| "BAND_RANGE"
	| "RATE_OUT_OF_BAND"
	| "VALID_FROM_IN_PAST"
	| "PARTNER_REQUIRED"
	| "PATTERN_INVALID"
	| "NOTES_REQUIRED"
	| "OVERLAP"
	| "COUNTRY_NOT_CONFIGURED";

export const countryTaxConfigAPI = {
	/** The row in force for a country (today, or as at a date). Null when the country is not configured. */
	getForCountry: async (countryCode: string, asAt?: string): Promise<CountryTaxConfig | null> => {
		try {
			const response = await axiosInstance.get(
				`/country-tax-config/${encodeURIComponent(countryCode.toUpperCase())}`,
				{
					params: asAt ? { asAt } : undefined,
				},
			);
			return response.data;
		} catch (error) {
			if (isApiErrorCode(error, "COUNTRY_NOT_CONFIGURED", 404)) return null;
			throw error;
		}
	},
	listAll: async (): Promise<CountryTaxConfigRow[]> => (await axiosInstance.get("/admin/country-tax-config")).data,
	create: async (input: CountryTaxConfigInput): Promise<CountryTaxConfigRow> =>
		(await axiosInstance.post("/admin/country-tax-config", input)).data,
	/** Ends the current row (valid_to); the note is the audit reason. */
	end: async (id: string, validTo: string, notes: string): Promise<CountryTaxConfigRow> =>
		(await axiosInstance.patch(`/admin/country-tax-config/${id}/end`, { validTo, notes })).data,
};
